import { useState, type FormEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Home, Pencil } from "lucide-react";

import { hostelService, type Hostel } from "../../services/hostelService";
import { apiErrorMessage } from "../../utils/apiError";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { errorLine } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { byText, type RegisterConfig } from "../register/registerLogic";
import { HOSTEL_TYPES, clock, hostelStatus, labelFrom } from "./hostelLogic";

const FACILITIES: [keyof Hostel, string][] = [
  ["has_security", "Night guard"],
  ["has_laundry", "Laundry"],
  ["has_study_room", "Prep room"],
  ["has_kitchen", "Kitchen"],
  ["has_recreation_room", "Common room"],
  ["has_wifi", "Wi-Fi"],
];

const residenceConfig: RegisterConfig<Hostel> = {
  search: (row) => [row.name, row.address, row.warden_name].join(" "),
  views: [
    { key: "open", label: "Open", match: (row) => row.is_active },
    { key: "space", label: "Beds free", match: (row) => row.is_active && row.current_occupancy < row.capacity },
    { key: "closed", label: "Closed", match: (row) => !row.is_active },
    { key: "all", label: "All" },
  ],
  category: { label: "For", value: (row) => labelFrom(HOSTEL_TYPES, row.hostel_type) },
  sorts: [{ key: "name", label: "Name (A–Z)", compare: byText<Hostel>((row) => row.name) }],
};

function ResidenceForm({ hostel, onClose, onDone }: { hostel?: Hostel; onClose: () => void; onDone: (row: Hostel) => void }) {
  const save = useMutation({
    mutationFn: (data: Record<string, unknown>) => (hostel ? hostelService.updateHostel({ id: hostel.id, data }) : hostelService.createHostel(data)),
    onSuccess: onDone,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim() || null;
    const data: Record<string, unknown> = {
      name: text("name"),
      hostel_type: text("hostel_type"),
      address: text("address"),
      capacity: Number(text("capacity")),
      phone: text("phone"),
      email: text("email"),
      curfew_time: text("curfew_time"),
      lights_out_time: text("lights_out_time"),
      rules: text("rules"),
      description: text("description"),
    };
    for (const [key] of FACILITIES) data[key] = form.get(key) === "on";
    if (!hostel) data.is_active = true;
    save.mutate(data);
  };
  const value = (name: keyof Hostel) => (hostel?.[name] as string | number | null | undefined) ?? "";
  return (
    <FormModal
      id="residence"
      title={hostel ? `Edit ${hostel.name}` : "Add a residence"}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The residence could not be saved.") : undefined}
      saveLabel={hostel ? "Save changes" : "Add the residence"}
      note={hostel ? undefined : "Add its rooms and beds next, under Rooms."}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The residence" divided={false}>
        <Field label="Name" hint="e.g. Mary Stuart House, Boys' Dormitory A.">
          <input required name="name" maxLength={100} defaultValue={value("name")} className="input mt-1" />
        </Field>
        <Field label="For">
          <select name="hostel_type" defaultValue={value("hostel_type") || "girls"} className="input mt-1">
            {HOSTEL_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Beds in all" hint={hostel ? `At least ${hostel.current_occupancy}, the boarders now.` : "Rooms' beds must add up to no more than this."}>
          <input required type="number" name="capacity" min={Math.max(1, hostel?.current_occupancy ?? 1)} step={1} defaultValue={value("capacity")} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Where" hint="Building or part of the school.">
          <input required name="address" defaultValue={value("address")} className="input mt-1" />
        </Field>
        <Field label="Phone" optional>
          <input type="tel" name="phone" maxLength={20} defaultValue={value("phone")} className="input mt-1" />
        </Field>
        <Field label="Email" optional>
          <input type="email" name="email" defaultValue={value("email")} className="input mt-1" />
        </Field>
      </FormSection>
      <FormSection title="Routine">
        <Field label="Back in by" optional>
          <input type="time" name="curfew_time" defaultValue={clock(hostel?.curfew_time)} className="input mt-1" />
        </Field>
        <Field label="Lights out" optional>
          <input type="time" name="lights_out_time" defaultValue={clock(hostel?.lights_out_time)} className="input mt-1" />
        </Field>
        <div className="grid gap-2 text-sm text-gray-700 sm:col-span-2 sm:grid-cols-3">
          {FACILITIES.map(([key, label]) => (
            <label key={key} className="flex items-center gap-2">
              <input type="checkbox" name={key} defaultChecked={hostel ? Boolean(hostel[key]) : key !== "has_wifi" && key !== "has_kitchen"} className="h-4 w-4 rounded border-gray-300" /> {label}
            </label>
          ))}
        </div>
        <Field label="House rules" optional className="sm:col-span-2" hint="Shown to families.">
          <textarea name="rules" rows={3} defaultValue={value("rules")} className="input mt-1" />
        </Field>
        <Field label="Description" optional className="sm:col-span-2">
          <textarea name="description" rows={2} defaultValue={value("description")} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** The residences, how full each is, their routine and rules. */
export function ResidencesPanel({ hostels, isLoading, isError, onRetry, canManage, canOperate, onChanged }: {
  hostels?: Hostel[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  canOperate: boolean;
  onChanged: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<Hostel | "new" | null>(null);
  const occupancy = useQuery({ queryKey: ["hostel-occupancy", viewing], queryFn: () => hostelService.getOccupancyStats(viewing!), enabled: viewing !== null });
  const inside = useQuery({ queryKey: ["hostel-active-visitors", viewing], queryFn: () => hostelService.getActiveVisitors(viewing!), enabled: viewing !== null && canOperate });
  const toggle = useMutation({
    mutationFn: (row: Hostel) => hostelService.updateHostel({ id: row.id, data: { is_active: !row.is_active } }),
    onSuccess: (row) => {
      setNotice({ tone: "success", text: row.is_active ? `${row.name} is open again.` : `${row.name} is closed; no new boarders can be placed there.` });
      onChanged();
    },
  });

  const columns: RegisterColumn<Hostel>[] = [
    { header: "Residence", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name, className: "min-w-[10rem]" },
    { header: "For", cell: (row) => labelFrom(HOSTEL_TYPES, row.hostel_type), exportValue: (row) => labelFrom(HOSTEL_TYPES, row.hostel_type), className: "text-gray-700" },
    { header: "Boarders", cell: (row) => `${row.current_occupancy} / ${row.capacity}`, exportValue: (row) => row.current_occupancy, align: "right" },
    { header: "Rooms", cell: (row) => `${row.occupied_rooms} of ${row.total_rooms} in use`, exportValue: (row) => row.total_rooms, className: "text-gray-700" },
    { header: "Back in by", cell: (row) => clock(row.curfew_time) || "—", exportValue: (row) => clock(row.curfew_time), className: "tabular-nums text-gray-700" },
    { header: "Where", cell: (row) => row.address, exportValue: (row) => row.address, className: "min-w-[10rem] whitespace-normal text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot {...hostelStatus(row)} />, exportValue: (row) => hostelStatus(row).label },
  ];

  const hostel = viewing !== null ? hostels?.find((row) => row.id === viewing) : undefined;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Residences"
        description={canManage ? "Open a residence for how full it is, its routine and rules, or to edit it." : "The residence, its routine and its rules."}
        actions={canManage && (
          <button type="button" onClick={() => setEditing("new")} className="btn-primary h-8 min-h-8 px-3 text-xs">
            <Home className="mr-1.5 h-3.5 w-3.5" /> Add a residence
          </button>
        )}
        rows={hostels}
        config={residenceConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          toggle.reset();
          setViewing(row.id);
        }}
        noun={["residence", "residences"]}
        exportName={{ file: "residences", title: "Boarding Residences" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "No residences yet. Add the first, then its rooms and beds." : "No residence to show."}
      />

      {hostel && !editing && (
        <DetailsModal
          title={hostel.name}
          headline={{ label: "Boarders", value: `${hostel.current_occupancy} of ${hostel.capacity}`, note: `${labelFrom(HOSTEL_TYPES, hostel.hostel_type)} · ${hostel.address}` }}
          status={hostelStatus(hostel)}
          facts={[
            ["Rooms in use", occupancy.data ? `${occupancy.data.occupied_rooms} of ${occupancy.data.total_rooms}` : `${hostel.occupied_rooms} of ${hostel.total_rooms}`],
            ["Beds set up", occupancy.data ? `${occupancy.data.total_beds} (${occupancy.data.available_beds} free)` : undefined],
            ["Back in by", clock(hostel.curfew_time) || undefined],
            ["Lights out", clock(hostel.lights_out_time) || undefined],
            ["Matron / warden", hostel.warden_name],
            ["Phone", hostel.phone ?? undefined],
            ["Has", FACILITIES.filter(([key]) => hostel[key]).map(([, label]) => label).join(", ") || undefined],
            ...(canOperate ? ([["Visitors inside now", inside.data ? String(inside.data.length) : "…"]] as [string, string][]) : []),
          ]}
          sections={[
            { title: "House rules", body: hostel.rules },
            { title: "About", body: hostel.description },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && (
                <button type="button" disabled={toggle.isPending || (hostel.is_active && hostel.current_occupancy > 0)} title={hostel.is_active && hostel.current_occupancy > 0 ? "Check the boarders out first." : undefined} onClick={() => toggle.mutate(hostel)} className="btn btn-outline">
                  {hostel.is_active ? "Close the residence" : "Open it again"}
                </button>
              )}
              {canManage && (
                <button type="button" className="btn btn-primary gap-1.5" onClick={() => setEditing(hostel)}>
                  <Pencil className="h-4 w-4" /> Edit
                </button>
              )}
            </>
          }
        >
          {toggle.isError && errorLine(toggle.error, "The residence could not be changed.")}
          {canOperate && inside.data && inside.data.length > 0 && (
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Visitors inside now</h4>
              <ul className="mt-1.5 divide-y divide-gray-100 rounded-lg border border-gray-200">
                {inside.data.map((visit) => (
                  <li key={visit.id} className="px-3 py-2 text-sm">
                    <span className="font-medium text-gray-900">{visit.visitor_name}</span> visiting {visit.student_name} · in at {clock(visit.visit_time_in)}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </DetailsModal>
      )}

      {editing && (
        <ResidenceForm
          hostel={editing === "new" ? undefined : editing}
          onClose={() => setEditing(null)}
          onDone={(saved) => {
            const created = editing === "new";
            setEditing(null);
            setNotice({ tone: "success", text: created ? `${saved.name} added. Add its rooms and beds under Rooms.` : `${saved.name} updated.` });
            setViewing(saved.id);
            onChanged();
          }}
        />
      )}
    </div>
  );
}
